import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useGameEngine, useProviders, getGameImage, getProviderImage } from 'hooks/useGameEngine';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet';
import GameImage from '../components/GameImage';
import { ChevronDown, SlidersHorizontal, ArrowDownNarrowWide, Layers, ArrowLeft, Play } from 'lucide-react';

// ─── Styled Components (same design as GroupPage) ──────────────────────────────

const PageWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding: 24px 16px 32px 16px;

  @media (min-width: 1024px) {
    padding: 24px 32px 48px 32px;
  }
`;

const ProviderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #21272b;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  color: #94a6b5;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 14px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  white-space: nowrap;

  &:hover { color: #fff; border-color: rgba(255,255,255,0.15); }
`;

const ProviderLogoBox = styled.div`
  width: 64px;
  height: 64px;
  background: #21272b;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
  }
`;

const ProviderMeta = styled.div`
  flex: 1;
`;

const GiantHeader = styled.h1`
  font-size: 2.25rem;
  @media (min-width: 1024px) { font-size: 3rem; }
  font-weight: 900;
  color: #ffffff;
  margin: 0 0 6px;
  letter-spacing: -0.05em;
  text-transform: uppercase;
`;

const ProviderSub = styled.p`
  color: #94a6b5;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  padding: 0;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const StyledSearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a6b5;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg { width: 18px; height: 18px; }
`;

const StyledSearchInput = styled.input`
  width: 100%;
  background-color: #292d2e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 16px 12px 44px;
  color: white;
  font-size: 15px;
  font-weight: 500;
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: #2f4553; }
  &::placeholder { color: #7f898c; }
`;

const FilterSortRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin-top: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TextButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #94a6b5;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.2s;

  &:hover { color: #ffffff; }
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const PillDropdown = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #0f212e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 8px 16px;
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover { border-color: rgba(255, 255, 255, 0.1); }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background-color: #0F1E25;
  border: 1px solid rgba(47, 69, 83, 0.4);
  border-radius: 6px;
  width: 220px;
  max-height: 280px;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  z-index: 50;
  padding: 4px;
`;

const DropdownItem = styled.button`
  width: 100% !important;
  text-align: left !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  background: transparent !important;
  border: none !important;
  font-size: 14px !important;
  line-height: 20px !important;
  font-weight: 600 !important;
  color: ${p => p.active ? '#ffffff' : '#8e969f'} !important;
  background-color: ${p => p.active ? 'rgba(47, 69, 83, 0.5)' : 'transparent'} !important;
  cursor: pointer !important;
  margin: 0 !important;
  transition: all 0.15s ease !important;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;

  &:hover {
    background-color: rgba(47, 69, 83, 0.5) !important;
    color: #ffffff !important;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 0;

  @media (min-width: 480px)  { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 768px)  { grid-template-columns: repeat(5, 1fr); }
  @media (min-width: 1024px) { grid-template-columns: repeat(7, 1fr); }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  transition: transform 0.2s; min-width: 0; width: 100%;

  &:hover { transform: translateY(-2px); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonCard = styled.div`
  background: linear-gradient(90deg, #213743 25%, #2f4553 50%, #213743 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-radius: 12px;
  aspect-ratio: 3 / 4;
  border: 1px solid rgba(255,255,255,0.04);
`;

const GameCard = styled.div`
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  background-color: #292d2e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: border-color 0.2s ease;
  aspect-ratio: 3 / 4;

  &:hover { border-color: rgba(255, 255, 255, 0.1); }
  &:active { transform: scale(0.95); }

  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    transition: transform 0.5s ease;
  }

  &:hover img { transform: scale(1.08); }
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  ${GameCard}:hover & { opacity: 1; }

  .play-icon {
    font-size: 24px;
    color: white;
    background: rgba(0,0,0,0.7);
    border-radius: 50%;
    padding: 12px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const PlayerOverlay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0 4px;
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #25de82;
  box-shadow: 0 0 8px rgba(37, 222, 130, 0.6);
`;

const PlayerText = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  margin-bottom: 32px;
  width: 100%;
  padding: 0;
`;

const Line = styled.div`
  flex: 1;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
`;

const LoadMoreButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.2s;

  &:hover { color: #ffffff; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #94a6b5;
`;

// ─── Main Component ────────────────────────────────────────────────────────────

const ProviderGamesPage = () => {
  const { providerId } = useParams();
  const history = useHistory();

  // Fetch provider info
  const { data: providers = [] } = useProviders();
  const provider = providers.find(p => String(p.id) === String(providerId) || p.provider_code === providerId);

  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [gamesList, setGamesList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const limit = 14;

  // Fetch games by provider_id
  const { data: games, isLoading } = useGameEngine({ 
    provider: providerId,
    search: searchQuery || undefined,
    limit: limit,
    offset: offset
  });

  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sortType, setSortType] = useState('Featured');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const sortRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setOffset(0);
    setGamesList([]);
    setHasMore(true);
    setSearchQuery('');
    setSortType('Featured');
  }, [providerId]);

  // Handle resets on search/filter changes
  useEffect(() => {
    setOffset(0);
    setGamesList([]);
    setHasMore(true);
  }, [searchQuery]);

  useEffect(() => {
    if (games) {
      if (offset === 0) {
        setGamesList(games);
      } else {
        setGamesList(prev => {
          const existingIds = new Set(prev.map(g => g.id));
          const newGames = games.filter(g => !existingIds.has(g.id));
          return [...prev, ...newGames];
        });
      }
      if (games.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    }
  }, [games, offset]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredGames = useMemo(() => {
    return gamesList || [];
  }, [gamesList]);

  const sortedGames = useMemo(() => {
    const list = [...filteredGames];
    if (sortType === 'A-Z') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortType === 'Z-A') list.sort((a, b) => b.name.localeCompare(a.name));
    return list;
  }, [filteredGames, sortType]);

  const displayGames = sortedGames;

  const gamePlayers = useMemo(() => {
    if (!games) return {};
    const counts = {};
    games.forEach(game => {
      const seed = game.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      counts[game.id] = String(50 + (seed % 650));
    });
    return counts;
  }, [games]);

  const providerName = provider?.name || String(providerId).replace(/-/g, ' ');
  const logoUrl = provider ? getProviderImage(provider) : null;

  return (
    <PageWrapper>
      <Helmet>
        <title>{providerName} Games — Casino</title>
        <meta name="description" content={`Browse all games from ${providerName}`} />
      </Helmet>

      <div className="casino-container mt-4">

        {/* Provider Header */}
        <ProviderHeader>
          <BackBtn onClick={() => history.push('/casino/providers')}>
            <ArrowLeft size={15} />
            Providers
          </BackBtn>

          <ProviderLogoBox>
            {logoUrl && !logoError ? (
              <img src={logoUrl} alt={providerName} onError={() => setLogoError(true)} />
            ) : (
              <Layers size={28} color="#4a5568" />
            )}
          </ProviderLogoBox>

          <ProviderMeta>
            <GiantHeader>{providerName}</GiantHeader>
            <ProviderSub>
              {isLoading ? 'Loading...' : `${(games || []).length} Games Available`}
            </ProviderSub>
          </ProviderMeta>
        </ProviderHeader>

        {/* Search */}
        <SearchInputWrapper>
          <StyledSearchIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </StyledSearchIcon>
          <StyledSearchInput
            type="text"
            placeholder={`Search in ${providerName}...`}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); }}
          />
        </SearchInputWrapper>

        {/* Filter / Sort Row */}
        <FilterSortRow>
          <ControlGroup>
            <TextButton>
              <SlidersHorizontal size={16} /> Filter
            </TextButton>
          </ControlGroup>
          <ControlGroup>
            <TextButton>
              <ArrowDownNarrowWide size={16} /> Sort
            </TextButton>
            <DropdownWrapper ref={sortRef}>
              <PillDropdown onClick={() => setSortDropdownOpen(!sortDropdownOpen)}>
                {sortType} <ChevronDown size={14} />
              </PillDropdown>
              {sortDropdownOpen && (
                <DropdownMenu style={{ left: 'auto', right: 0 }}>
                  {['Featured', 'A-Z', 'Z-A'].map(type => (
                    <DropdownItem
                      key={type}
                      active={sortType === type}
                      onClick={() => { setSortType(type); setSortDropdownOpen(false); }}
                    >
                      {type}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              )}
            </DropdownWrapper>
          </ControlGroup>
        </FilterSortRow>

        {/* Games Grid */}
        {isLoading ? (
          <GridContainer>
            {Array.from({ length: 14 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </GridContainer>
        ) : displayGames.length > 0 ? (
          <>
            <GridContainer>
              {displayGames.map((game, i) => (
                <CardWrapper key={i}>
                  <GameCard onClick={() => history.push(`/play/${game.id}`)}>
                    <GameImage 
                      src={getGameImage(game)} 
                      alt={game.name} 
                    />
                    <PlayOverlay>
                      <div className="play-icon">
                        <Play size={20} fill="white" color="white" />
                      </div>
                    </PlayOverlay>
                  </GameCard>
                  <PlayerOverlay>
                    <PlayerText>{game.name}</PlayerText>
                  </PlayerOverlay>
                </CardWrapper>
              ))}
            </GridContainer>

            {hasMore && (
              <LoadMoreContainer>
                <Line />
                <LoadMoreButton onClick={() => setOffset(prev => prev + limit)}>
                  LOAD MORE
                </LoadMoreButton>
                <Line />
              </LoadMoreContainer>
            )}
          </>
        ) : (
          <EmptyState>
            <Layers size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>No games found</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>
              {searchQuery ? 'Try a different search term' : `No active games from ${providerName}`}
            </p>
          </EmptyState>
        )}
      </div>
    </PageWrapper>
  );
};

export default ProviderGamesPage;
